/**
 * Mobile Team Scanner
 * Dedicated mobile-optimized route for staff to scan QR codes
 * Supports: Check-in, Meal scanning, Housing/Accommodation, Stock In/Out
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import LanguageSwitcher from '../components/LanguageSwitcher';
import StockScanner from '../components/StockScanner';
import QRAccessScanner from '../components/QRAccessScanner';
import { getToken } from '@/lib/authToken';
import { 
  Camera, CameraOff, CheckCircle, XCircle, RefreshCw, 
  UserCheck, Utensils, Home, ArrowLeft, Wifi, WifiOff,
  ScanLine, ChevronRight, Package, Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SCAN_MODES = {
  CHECKIN: 'checkin',
  MEAL: 'meal',
  HOUSING: 'housing',
  STOCK: 'stock',
  ACCESS: 'access'
};

const MobileScanner = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [event, setEvent] = useState(null);
  const [mode, setMode] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [stats, setStats] = useState({ scanned: 0, today: 0 });
  
  const html5QrCodeRef = useRef(null);
  const audioContextRef = useRef(null);

  useEffect(() => {
    fetchEvent();
    
    // Network status listeners
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      stopScanner();
    };
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      const response = await fetch(`${API_URL}/api/events/${eventId}`);
      if (response.ok) {
        const data = await response.json();
        setEvent(data);
      }
    } catch (err) {
      console.error('Failed to fetch event:', err);
    }
  };

  const playSound = useCallback((success) => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioContextRef.current;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.value = success ? 880 : 330;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.2);
    } catch (e) {
      console.log('Audio error:', e);
    }
  }, []);

  const startScanner = async () => {
    try {
      setScanning(true);
      setResult(null);

      html5QrCodeRef.current = new Html5Qrcode("mobile-qr-reader");
      
      await html5QrCodeRef.current.start(
        { facingMode: "environment" },
        {
          fps: 15,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0
        },
        onScanSuccess,
        () => {}
      );
    } catch (err) {
      console.error('Camera error:', err);
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
      } catch (e) {}
      html5QrCodeRef.current = null;
    }
    setScanning(false);
  };

  const onScanSuccess = useCallback(async (decodedText) => {
    if (html5QrCodeRef.current) {
      await html5QrCodeRef.current.pause();
    }

    const token = getToken();
    
    try {
      let endpoint = '';
      let body = {};
      
      switch (mode) {
        case SCAN_MODES.CHECKIN:
          endpoint = `${API_URL}/api/events/${eventId}/qr/scan`;
          body = { qr_data: decodedText };
          break;
        case SCAN_MODES.MEAL:
          endpoint = `${API_URL}/api/events/${eventId}/meals/scan`;
          body = { delegate_id: decodedText, meal_type: 'current' };
          break;
        case SCAN_MODES.HOUSING:
          endpoint = `${API_URL}/api/events/${eventId}/housing/checkin`;
          body = { delegate_id: decodedText };
          break;
        default:
          throw new Error('Invalid mode');
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (response.ok && (data.success !== false)) {
        playSound(true);
        setResult({
          success: true,
          name: data.delegate?.name || data.name || 'Guest',
          message: data.message || t('scanner.success')
        });
        setStats(prev => ({ ...prev, scanned: prev.scanned + 1 }));
      } else {
        playSound(false);
        setResult({
          success: false,
          message: data.message || data.error || t('scanner.error')
        });
      }
    } catch (err) {
      playSound(false);
      setResult({
        success: false,
        message: isOnline ? t('errors.somethingWentWrong') : t('offline.youAreOffline')
      });
    }

    // Auto-reset after 3 seconds
    setTimeout(() => {
      setResult(null);
      if (html5QrCodeRef.current && scanning) {
        html5QrCodeRef.current.resume();
      }
    }, 3000);
  }, [mode, eventId, isOnline, scanning, playSound, t]);

  const handleModeSelect = (selectedMode) => {
    setMode(selectedMode);
  };

  const handleBack = () => {
    if (scanning) {
      stopScanner();
    }
    if (mode) {
      setMode(null);
    } else {
      navigate(-1);
    }
  };

  // Mode Selection Screen
  if (!mode) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-white p-4 safe-area-top safe-area-bottom">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-white">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            {isOnline ? (
              <Badge variant="outline" className="text-green-400 border-green-400">
                <Wifi className="h-3 w-3 mr-1" /> {t('offline.backOnline', 'Online')}
              </Badge>
            ) : (
              <Badge variant="outline" className="text-amber-400 border-amber-400">
                <WifiOff className="h-3 w-3 mr-1" /> {t('checkIn.offline')}
              </Badge>
            )}
            <LanguageSwitcher variant="ghost" showLabel={false} />
          </div>
        </div>

        {/* Event Name */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold">{event?.name || t('events.title')}</h1>
          <p className="text-gray-400">{t('scanner.title')}</p>
        </div>

        {/* Mode Selection */}
        <div className="space-y-4">
          <h2 className="text-lg font-medium text-center mb-4">{t('scanner.selectMode')}</h2>
          
          <Card 
            className="bg-gradient-to-r from-indigo-600 to-indigo-500 border-0 cursor-pointer hover:scale-[1.02] transition-transform"
            onClick={() => handleModeSelect(SCAN_MODES.CHECKIN)}
            data-testid="mode-checkin"
          >
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 rounded-xl p-3">
                  <UserCheck className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{t('scanner.checkInMode')}</h3>
                  <p className="text-indigo-200 text-sm">{t('checkIn.scanInstructions')}</p>
                </div>
              </div>
              <ChevronRight className="h-6 w-6 text-white/70" />
            </CardContent>
          </Card>

          <Card 
            className="bg-gradient-to-r from-emerald-600 to-emerald-500 border-0 cursor-pointer hover:scale-[1.02] transition-transform"
            onClick={() => handleModeSelect(SCAN_MODES.MEAL)}
            data-testid="mode-meal"
          >
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 rounded-xl p-3">
                  <Utensils className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{t('scanner.mealMode')}</h3>
                  <p className="text-emerald-200 text-sm">{t('meals.scanMeal')}</p>
                </div>
              </div>
              <ChevronRight className="h-6 w-6 text-white/70" />
            </CardContent>
          </Card>

          <Card 
            className="bg-gradient-to-r from-amber-600 to-amber-500 border-0 cursor-pointer hover:scale-[1.02] transition-transform"
            onClick={() => handleModeSelect(SCAN_MODES.HOUSING)}
            data-testid="mode-housing"
          >
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 rounded-xl p-3">
                  <Home className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{t('scanner.housingMode')}</h3>
                  <p className="text-amber-200 text-sm">{t('housing.title')}</p>
                </div>
              </div>
              <ChevronRight className="h-6 w-6 text-white/70" />
            </CardContent>
          </Card>

          <Card 
            className="bg-gradient-to-r from-purple-600 to-purple-500 border-0 cursor-pointer hover:scale-[1.02] transition-transform"
            onClick={() => handleModeSelect(SCAN_MODES.STOCK)}
            data-testid="mode-stock"
          >
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 rounded-xl p-3">
                  <Package className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{t('scanner.stockMode', 'Stock In/Out')}</h3>
                  <p className="text-purple-200 text-sm">{t('scanner.stockDescription', 'Track inventory')}</p>
                </div>
              </div>
              <ChevronRight className="h-6 w-6 text-white/70" />
            </CardContent>
          </Card>

          <Card 
            className="bg-gradient-to-r from-cyan-600 to-cyan-500 border-0 cursor-pointer hover:scale-[1.02] transition-transform"
            onClick={() => handleModeSelect(SCAN_MODES.ACCESS)}
            data-testid="mode-access"
          >
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 rounded-xl p-3">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{t('scanner.accessMode', 'Access Verification')}</h3>
                  <p className="text-cyan-200 text-sm">{t('scanner.accessDescription', 'View all permissions')}</p>
                </div>
              </div>
              <ChevronRight className="h-6 w-6 text-white/70" />
            </CardContent>
          </Card>
        </div>

        {/* Stats */}
        <div className="mt-8 bg-gray-800/50 rounded-xl p-4">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <div className="text-3xl font-bold text-white">{stats.scanned}</div>
              <div className="text-sm text-gray-400">{t('scanner.thisSession', 'This Session')}</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">{stats.today}</div>
              <div className="text-sm text-gray-400">{t('scanner.today', 'Today')}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Stock mode renders a different component
  if (mode === SCAN_MODES.STOCK) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex flex-col safe-area-top safe-area-bottom">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-500 p-4">
          <div className="flex justify-between items-center">
            <Button variant="ghost" size="sm" onClick={handleBack} className="text-white">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              <span className="font-medium">{t('scanner.stockMode', 'Stock In/Out')}</span>
            </div>
            <LanguageSwitcher variant="ghost" showLabel={false} />
          </div>
        </div>
        {/* Stock Scanner Component */}
        <div className="flex-1 overflow-auto">
          <StockScanner eventId={eventId} />
        </div>
      </div>
    );
  }

  // Access Verification mode - uses dedicated full-screen component
  if (mode === SCAN_MODES.ACCESS) {
    return (
      <QRAccessScanner 
        eventId={eventId} 
        onClose={handleBack}
      />
    );
  }

  // Scanner Screen for other modes
  const modeColors = {
    [SCAN_MODES.CHECKIN]: { bg: 'from-indigo-600 to-indigo-500', icon: UserCheck },
    [SCAN_MODES.MEAL]: { bg: 'from-emerald-600 to-emerald-500', icon: Utensils },
    [SCAN_MODES.HOUSING]: { bg: 'from-amber-600 to-amber-500', icon: Home },
  };
  const currentMode = modeColors[mode];
  const ModeIcon = currentMode?.icon || UserCheck;

  if (!currentMode) {
    // Fallback if mode not found
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col safe-area-top safe-area-bottom">
      {/* Header */}
      <div className={`bg-gradient-to-r ${currentMode.bg} p-4`}>
        <div className="flex justify-between items-center">
          <Button variant="ghost" size="sm" onClick={handleBack} className="text-white">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <ModeIcon className="h-5 w-5" />
            <span className="font-medium">
              {mode === SCAN_MODES.CHECKIN && t('scanner.checkInMode')}
              {mode === SCAN_MODES.MEAL && t('scanner.mealMode')}
              {mode === SCAN_MODES.HOUSING && t('scanner.housingMode')}
            </span>
          </div>
          <LanguageSwitcher variant="ghost" showLabel={false} />
        </div>
      </div>

      {/* Scanner Area */}
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        {!scanning ? (
          <div className="text-center">
            <div className="relative inline-block mb-6">
              <div className={`w-24 h-24 bg-gradient-to-br ${currentMode.bg} rounded-2xl flex items-center justify-center`}>
                <Camera className="h-12 w-12 text-white" />
              </div>
              <div className="absolute -bottom-2 -right-2 bg-white rounded-full p-2">
                <ScanLine className="h-5 w-5 text-gray-900" />
              </div>
            </div>
            <h2 className="text-2xl font-bold mb-2">{t('checkIn.readyToScan')}</h2>
            <p className="text-gray-400 mb-6">{t('scanner.tapToScan')}</p>
            <Button 
              onClick={startScanner} 
              size="lg"
              className={`text-lg px-8 py-6 bg-gradient-to-r ${currentMode.bg}`}
              data-testid="start-scanner-btn"
            >
              <Camera className="h-5 w-5 mr-2" />
              {t('checkIn.startScanner')}
            </Button>
          </div>
        ) : (
          <div className="relative w-full max-w-sm">
            <div className="bg-black rounded-2xl overflow-hidden shadow-2xl">
              <div id="mobile-qr-reader" className="w-full aspect-square" />
              
              {/* Result Overlay */}
              {result && (
                <div 
                  className={`absolute inset-0 flex items-center justify-center ${
                    result.success ? 'bg-green-500/95' : 'bg-red-500/95'
                  }`}
                  data-testid="scan-result"
                >
                  <div className="text-center p-6">
                    {result.success ? (
                      <CheckCircle className="h-16 w-16 mx-auto text-white mb-4" />
                    ) : (
                      <XCircle className="h-16 w-16 mx-auto text-white mb-4" />
                    )}
                    <div className="text-xl font-bold text-white mb-2">
                      {result.success ? t('scanner.success') : t('scanner.error')}
                    </div>
                    {result.name && (
                      <div className="text-lg text-white/90">{result.name}</div>
                    )}
                    <div className="text-sm text-white/70 mt-2">{result.message}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="mt-4 flex justify-center">
              <Button 
                variant="outline" 
                onClick={stopScanner}
                className="text-white border-gray-600"
                data-testid="stop-scanner-btn"
              >
                <CameraOff className="h-4 w-4 mr-2" />
                {t('checkIn.stopScanner')}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Stats */}
      <div className="bg-gray-800 p-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            {isOnline ? (
              <Badge variant="outline" className="text-green-400 border-green-400">
                <Wifi className="h-3 w-3 mr-1" /> {t('checkIn.onlineStatus')}
              </Badge>
            ) : (
              <Badge variant="outline" className="text-amber-400 border-amber-400">
                <WifiOff className="h-3 w-3 mr-1" /> {t('checkIn.offlineStatus')}
              </Badge>
            )}
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">{stats.scanned}</div>
            <div className="text-xs text-gray-400">{t('scanner.scanned', 'Scanned')}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileScanner;
